import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { noop } from 'ui-components/default-props/noop';
import { Text, TextType } from 'ui-components/components/text/index';
import { LabelWithRequiredIndicator } from 'ui-components/components/label-with-required-indicator';

import { messages as pickupLocationDropdownMessages } from '../../modal-components/shipfrom-dropdown/messages';
import { messages as pickupLocationMessages } from '../../modal-components/pickup-location/messages';
import styles from './style.module.css';

const PickupLocationAddress = ({ shipFrom, textType, isTwoRows = false }) => {
  const { line1 = '', line2 = '', city = '', state = '', postalCode = '', countryCode = '' } = shipFrom.originAddress;

  let addressLines = `${line1}`;
  if (line2) addressLines += ` ${line2}`;

  if (isTwoRows) {
    return (
      <>
        <Text textType={textType} value={addressLines} />
        <Text textType={textType} value={`${city}, ${state} ${postalCode}, ${countryCode}`} />
      </>
    );
  }

  return <Text textType={textType} value={`${addressLines}, ${city}, ${state}, ${postalCode}, ${countryCode}`} />;
};

/**
 * PickupLocationDropdown component for selecting pickup location
 * @param {object} props
 * @param {string} [props.className] - Additional CSS class name
 * @param {boolean} [props.hasError] - Whether the field has an error
 * @param {React.ReactNode} [props.inlineMessage] - Inline error message to display
 * @param {string} [props.inlineMessageType] - Type of inline message
 * @param {(shipFromId: number) => void} [props.onShipFromSelected] - Callback when ship from location is selected
 * @param {number} [props.selectedShipFromId] - Currently selected ship from ID
 * @param {Array} [props.shipFroms] - Array of available ship from locations
 * @returns {React.ReactElement} The PickupLocationDropdown component
 */
export const PickupLocationDropdown = ({
  className = undefined,
  hasError = false,
  inlineMessage,
  inlineMessageType = '',
  onShipFromSelected = noop,
  selectedShipFromId = undefined,
  shipFroms = [],
}) => {
  const selectedShipFrom = useMemo(() => {
    return shipFroms.find((shipFrom) => shipFrom.shipFromId === selectedShipFromId);
  }, [shipFroms, selectedShipFromId]);

  const togglerText = useMemo(() => {
    if (selectedShipFrom) {
      return selectedShipFrom.name;
    }
    return shipFroms && shipFroms.length > 0 ? (
      <i>
        <FormattedMessage {...pickupLocationDropdownMessages.pickupLocationPlaceholder} />
      </i>
    ) : (
      <i>
        <FormattedMessage {...pickupLocationDropdownMessages.pickupLocationEmptyPlaceholder} />
      </i>
    );
  }, [selectedShipFrom, shipFroms]);

  return (
    <div>
      <LabelWithRequiredIndicator>
        <Text textType={TextType.Label} value={<FormattedMessage {...pickupLocationMessages.pickupLocationLabel} />} />
      </LabelWithRequiredIndicator>
      <DropdownMenu
        className={className}
        dropdownMenuShouldHaveWidthOfButton
        fullWidth
        hasError={hasError}
        inlineMessage={inlineMessage}
        inlineMessageType={inlineMessageType}
        variant='form'
      >
        <DropdownMenuToggler>{togglerText}</DropdownMenuToggler>
        {shipFroms.map((shipFrom) => (
          <DropdownMenuItem
            key={shipFrom.shipFromId}
            id={shipFrom.shipFromId}
            onClick={() => onShipFromSelected(shipFrom.shipFromId)}
            selected={shipFrom.shipFromId === selectedShipFromId}
          >
            {shipFrom.name}
            <div className={styles['item-subtitle']}>
              <PickupLocationAddress isTwoRows shipFrom={shipFrom} textType={TextType.Caption} />
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
      {selectedShipFromId && (
        <div className={styles['selected-address']}>
          <PickupLocationAddress shipFrom={selectedShipFrom} textType={TextType.Paragraph} />
        </div>
      )}
    </div>
  );
};
